'use client'

import { useCallback } from 'react'
import { useTranslations } from 'next-intl'
import cn from 'classnames'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'
import quantityStyles from '../QuantitySelection/index.module.scss'

import SummaryBox from '@booking/components/SummaryBox'
import HelpBox from '@booking/components/HelpBox'
import BackButton from '@booking/components/BackButton'
import ContinueButton from '@booking/components/ContinueButton'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'

// ---------------------------------------------------------------------------
// Addon definitions — add new addons here, keyed by their API identifier.
// The config just lists which keys to show; display detail lives here.

type AddonDef = {
  title: string
  description: string
  price?: string
}

function useAddonDef(key: string): AddonDef | null {
  const t = useTranslations('booking')

  const defs: Record<string, AddonDef> = {
    insurance: {
      title: t('addons.insurance.title'),
      description: t('addons.insurance.description'),
      price: t('addons.insurance.price'),
    },
  }

  return defs[key] ?? null
}

// ---------------------------------------------------------------------------

export default function AddonsSelection() {
  const t = useTranslations('booking')
  const state = useBookingState()
  const { dispatch, onCompleteAddonsSelection } = useBookingActionsCtx()

  const addonsConfig = state.config.addons
  const selectedAddons = state.selectedAddons

  const toggleAddon = useCallback(
    (key: string) => {
      const next = selectedAddons.includes(key)
        ? selectedAddons.filter((k) => k !== key)
        : [...selectedAddons, key]
      dispatch({ type: 'SET_SELECTED_ADDONS', selectedAddons: next })
    },
    [selectedAddons, dispatch],
  )

  const onContinue = useCallback(() => {
    return onCompleteAddonsSelection()
  }, [onCompleteAddonsSelection])

  return (
    <div className={cn(shared.box)}>
      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)}>
          <div className={cn(styles.header)}>
            <BackButton />
            {addonsConfig?.titleKey && (
              <h2 className={cn(styles.title)}>{t(addonsConfig.titleKey)}</h2>
            )}
            {addonsConfig?.subtitleKey && (
              <p className={cn(quantityStyles.note)}>{t(addonsConfig.subtitleKey)}</p>
            )}
          </div>

          <ul className={styles.addonList}>
            {addonsConfig?.items.map((key) => (
              <AddonCard
                key={key}
                addonKey={key}
                selected={selectedAddons.includes(key)}
                onToggle={toggleAddon}
              />
            ))}
          </ul>

          <ContinueButton className={cn(shared.continue)} onContinue={onContinue} />
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox onContinue={onContinue} />
          <HelpBox />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

type AddonCardProps = {
  addonKey: string
  selected: boolean
  onToggle: (key: string) => void
}

function AddonCard({ addonKey, selected, onToggle }: AddonCardProps) {
  const def = useAddonDef(addonKey)
  if (!def) return null

  return (
    <li
      className={cn(styles.addonCard, selected && styles.addonCardSelected)}
      onClick={() => onToggle(addonKey)}
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && onToggle(addonKey)}
    >
      <div className={styles.addonInfo}>
        <strong className={styles.addonTitle}>{def.title}</strong>
        <p className={styles.addonDescription}>{def.description}</p>
        {def.price && <span className={styles.addonPrice}>{def.price}</span>}
      </div>

      <div className={cn(styles.checkbox, selected && styles.checkboxChecked)}>
        {selected && (
          <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 5L4.5 8.5L11 1"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </li>
  )
}
